import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch user's subscription and features
  useEffect(() => {
    if (user) {
      fetchUserSubscription();
      fetchUserFeatures();
      fetchSubscriptionTiers();
    }
  }, [user]);

  const fetchUserSubscription = async () => {
    try {
      const response = await axios.get(`${API}/api/user/subscription-info`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Set default starter subscription
      setSubscription({
        tier_id: 'starter',
        tier: 'starter',
        status: 'active',
        billing_cycle: 'monthly',
        is_admin: false
      });
    }
  };

  const fetchUserFeatures = async () => {
    try {
      const response = await axios.get(`${API}/api/user-features`);
      setFeatures(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching features:', error);
      // Set default starter features
      setFeatures({
        tier: 'starter',
        features: ['basic_planning', 'single_business_plan'],
        limits: {
          business_plans: 1,
          subsidiaries: 0,
          users: 1,
          scenarios: 1
        }
      });
      setLoading(false);
    }
  };

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await axios.get(`${API}/api/subscription-tiers`);
      setSubscriptionTiers(response.data);
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
      setSubscriptionTiers([]);
    }
  };

  // Check if user has access to a feature
  const hasFeature = (featureName) => {
    if (!features) return false;
    // Admins have access to all features
    if (subscription?.is_admin || subscription?.tier === 'admin' || features.tier === 'admin') return true;
    // Check for wildcard access (all features)
    if (features.features.includes('*') || features.features.includes('everything')) return true;
    return features.features.includes(featureName);
  };

  // Check if user is within usage limits
  const isWithinLimit = (limitType, currentUsage) => {
    // Admins have unlimited access
    if (subscription?.is_admin || subscription?.tier === 'admin' || features?.tier === 'admin') return true;
    if (!features || !features.limits) return true;
    const limit = features.limits[limitType];
    if (limit === -1) return true; // Unlimited
    return currentUsage < limit;
  };

  // Get limit for a specific resource
  const getLimit = (limitType) => {
    if (!features || !features.limits) return 0;
    const limit = features.limits[limitType];
    return limit === -1 ? 'Unlimited' : limit;
  };

  // Track feature usage
  const trackUsage = async (feature, count = 1) => {
    try {
      await axios.post(`${API}/api/track-usage?feature=${feature}&count=${count}`);
    } catch (error) {
      console.error('Error tracking usage:', error);
    }
  };

  // Upgrade subscription
  const upgradeSubscription = async (tierName, billingCycle = 'monthly') => {
    try {
      const targetTier = subscriptionTiers.find(t => t.name === tierName);
      if (!targetTier) {
        toast.error('Invalid subscription tier');
        return false;
      }

      const response = await axios.post(`${API}/api/user-subscription`, {
        user_id: user.id,
        tier_id: targetTier.id,
        billing_cycle: billingCycle
      });

      // Validate response data before setting state
      if (response.data && typeof response.data === 'object' && !response.data.type) {
        setSubscription(response.data);
      } else {
        throw new Error("Invalid subscription data received");
      }
      await fetchUserFeatures(); // Refresh features
      toast.success(`Successfully upgraded to ${targetTier.display_name}`);
      return true;
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      
      // Handle different types of errors
      let errorMessage = "Failed to upgrade subscription";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          console.error("Validation errors:", error.response.data.detail);
          errorMessage = "Subscription validation failed";
        } else {
          errorMessage = "Failed to upgrade subscription - validation error";
        }
      } else if (error.message) {
        errorMessage = `Upgrade failed: ${error.message}`;
      }
      
      toast.error(errorMessage);
      return false;
    }
  };

  // Get current tier info
  const getCurrentTier = () => {
    if (!subscription || !subscriptionTiers.length) return null;
    // Match by tier name or tier id
    return subscriptionTiers.find(t => 
      t.id === subscription.tier_id || 
      t.name === subscription.tier_id ||
      t.name === subscription.tier
    );
  };

  // Check if feature requires upgrade
  const requiresUpgrade = (featureName) => {
    return !hasFeature(featureName);
  };

  // Get upgrade suggestion for a feature
  const getUpgradeSuggestion = (featureName) => {
    const featureToTierMap = {
      'performance_tracking': 'professional',
      'scenario_modeling': 'professional', 
      'advanced_kpis': 'professional',
      'multi_subsidiary': 'business',
      'cost_centers': 'business',
      'business_intelligence': 'business',
      'advanced_reports': 'business',
      'api_access': 'enterprise',
      'custom_integrations': 'enterprise',
      'white_labeling': 'enterprise'
    };

    const requiredTier = featureToTierMap[featureName];
    if (!requiredTier) return null;

    return subscriptionTiers.find(t => t.name === requiredTier);
  };

  const value = {
    subscription,
    features,
    subscriptionTiers,
    loading,
    hasFeature,
    isWithinLimit,
    getLimit,
    trackUsage,
    upgradeSubscription,
    getCurrentTier,
    requiresUpgrade,
    getUpgradeSuggestion,
    refreshSubscription: fetchUserSubscription,
    refreshFeatures: fetchUserFeatures
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;